/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import moment from 'moment';

// Component imports
import { Button, Col, Collapse, Divider, Empty, Popconfirm, Row, Select, Skeleton, Space, Spin, Tooltip, Typography, Dropdown, Menu } from 'antd';
import { AppstoreAddOutlined, ArrowLeftOutlined, CloseSquareOutlined, DownloadOutlined, MessageOutlined, StarFilled, StarOutlined, UserAddOutlined } from '@ant-design/icons';
import ConversationNotesPanel from './ConversationNotesPanel';
import ConversationStatusTag from './ConversationStatusTag';
import CrmIntegrationsPanel from './CrmIntegrationsPanel';
import CustomInputAutomationForm from '../Forms/CustomInputAutomationForm';
import InviteAgentsPanel from './InviteAgentsPanel';
import PredefinedEntitiesPanel from './PredefinedEntitiesPanel';
import ResponsiveTagsList from '../UI/ResponsiveTagsList';
import UserInfoPanel from './UserInfoPanel';
import { SidebarCollapseIcon } from '../Icons/SidebarCollapseIcon';
import { LeaveConversationIcon } from '../Icons/LeaveConversationIcon';
import { ImportantFlag } from '../Icons/liveChatFlags/ImportantFlag';
import { AiAssistantIcon } from '../Icons/AiAssistantIcon';
import AiAssistant from './AiAssistant';
import JsonViewer from '../UI/JsonViewer';

// Misc imports
import createAutomationBody from '../../utils/misc/createAutomationBody';
import handleFileDownload from '../../utils/misc/handleFileDownload';
import is from '../../utils/is';
import isValidURL from '../../utils/string/isValidURL';
import { CONVERSATION_STATUSES, LIVECHAT_MODES, LIVECHAT_PARTICIPANT_TYPES, LIVECHAT_SCREEN_TABS, REQUEST_STATUSES } from '../../constants/liveChat';
import { LiveChatContext } from '../../screens/LiveChatScreen';
import { STORAGE_KEYS } from '../../constants/main';
import { capitalize } from '../../utils/string/capitalize';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { leaveConversation, sendConversationMessage, terminateConversation, updateFavoriteAutomations } from '../../store/liveChat/liveChatThunks';
import { revertBlobTypes } from '../../store/utils/revertBlobTypes';
import { uniqueBy } from '../../utils/array/uniqueBy';
import { useConversationStatus } from '../../utils/hooks/useConversationStatus';
import { useFetchConversationTagsQuery, useFetchOrganizationTagsQuery, useLazyExportConversationTranscriptCsvQuery, useLazyExportConversationTranscriptPdfQuery, useUpdateConversationTagsMutation, useUpdateOrganizationTagsMutation } from '../../store/liveChat/liveChatApi';
import { useTriggerAutomationMutation } from '../../store/predefinedEntities/predefinedEntitiesApi';
import { ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX } from '../../constants/regex';
import { IS_LIVECHAT_ASSISTANT_ENABLED } from '../../constants/env';
import { useFetchBotDeploymentsQuery } from '../../store/botDeployments/botDeploymentsApi';
import { isEmptyObject } from '../../utils/is/isEmptyObject';

// Constant declarations
const TABS = {
  ASSISTANT: 'assistant',
  INVITES: 'invites',
  AUTOMATIONS: 'automations',
  EXPORT: 'export',
  USER: 'user',
  END: 'end',
  LEAVE: 'leave'
};

const LiveChatSidebarRight = () => {
  const { orgId } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isRequestsTab = searchParams.get('tab') === LIVECHAT_SCREEN_TABS.NEW || searchParams.get('tab') === LIVECHAT_SCREEN_TABS.REQUESTS_HISTORY;

  const { selectedRequest, sidebarOpen, setSidebarOpen, isEditingResponses, setIsEditingResponses, isEditingAutomations, setIsEditingAutomations } = useContext(LiveChatContext);
  const { currentUser } = useSelector((state) => state.auth);
  const { favoriteAutomations } = useSelector((state) => state.livechat);
  const { data: automations } = getCurrentQueryState('fetchAutomations', { orgId });
  const { data: livechatSettings } = getCurrentQueryState('fetchLiveChatApplicationSettings', { orgId });
  const { currentData: incomingRequests } = getCurrentQueryState('fetchRequests', { orgId });
  const { currentData: activeConversationInfo, error: fetchActiveConversationError } = getCurrentQueryState('fetchActiveConversation', { orgId, conversationId: searchParams.get('conversation') });
  const { data: bots } = getCurrentQueryState('fetchBots', { orgId }, !orgId);
  const [triggerAutomation, { isLoading: isTriggeringAutomation }] = useTriggerAutomationMutation();
  const [exportConversationTranscriptPdf, { isLoading: isExportingTranscript }] = useLazyExportConversationTranscriptPdfQuery();

  const botExists = bots?.items.find(bot => bot.id === livechatSettings?.botAssistantSettings.tenantId) || false;

  const [exportConversationTranscriptCsv] = useLazyExportConversationTranscriptCsvQuery();
  const { currentData: sessionTags, isFetching: isLoadingSessionTags, isSuccess: hasTags } = useFetchConversationTagsQuery({ orgId, tenantId: activeConversationInfo?.tenantId, sessionId: activeConversationInfo?.platformSessionId }, { skip: !activeConversationInfo });
  const { data: organizationTags } = useFetchOrganizationTagsQuery({ orgId });
  const { data: botDeployments } = useFetchBotDeploymentsQuery({ orgId, tenantId: livechatSettings?.botAssistantSettings.tenantId }, { skip: !botExists || ![LIVECHAT_SCREEN_TABS.OPEN, LIVECHAT_SCREEN_TABS.CLOSED].includes(searchParams.get('tab')) || !livechatSettings?.botAssistantSettings.tenantId });
  const [updateSessionTags] = useUpdateConversationTagsMutation();
  const [updateOrganizationTags] = useUpdateOrganizationTagsMutation();

  const [hasJoined, isInactive] = useConversationStatus();
  const [organizationFavoriteAutomations, setOrganizationFavoriteAutomations] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [customAutomation, setCustomAutomation] = useState(null);
  const [hasMultipleActiveAgents, setHasMultipleActiveAgents] = useState(null);
  const endPopoverContainerRef = useRef(null);

  const assistantDeploymentExists = botDeployments?.items.find(deployment => deployment.id === livechatSettings?.botAssistantSettings.botDeploymentId) || false;
  useEffect(() => {
    if (favoriteAutomations && automations && !isRequestsTab) {
      const orgAutomations = favoriteAutomations.find(automationGroup => automationGroup.orgId === orgId)?.automations ?? [];
      setOrganizationFavoriteAutomations(automations.filter(automation => orgAutomations.find(item => item.id === automation.id)));
    }
  }, [favoriteAutomations, automations]);

  useEffect(() => {
    if (activeConversationInfo) {
      setHasMultipleActiveAgents(activeConversationInfo.livechatParticipants.filter(
        participant =>
          participant.status === 'active' && participant.participantType === 'agent'
      ).length > 1);
    }
  }, [activeConversationInfo]);

  useEffect(() => {
    if (automations) {
      setSelectedAutomation(null);
    }
  }, [automations]);

  useEffect(() => {
    if (activeTab) {
      setSelectedAutomation(null);
    }
  }, [activeTab]);

  const isOwner = useMemo(() => {
    return activeConversationInfo?.livechatParticipants.find(participant => participant.platformUserId === currentUser.id && participant.isOwner);
  }, [activeConversationInfo?.livechatParticipants]);

  const conversationExtras = isRequestsTab
    ? selectedRequest?.extras || {}
    : activeConversationInfo?.extras || {};
  const hasExtras = conversationExtras && !isEmptyObject(conversationExtras);

  const triggerEntity = (entity) => {
    if (entity?.text) {
      if (entity?.text?.trim()) {
        dispatch(sendConversationMessage({
          text: entity?.text?.trim(),
          livechatConversation: {
            id: activeConversationInfo.id
          }
        }));
      }
    } else {
      setCustomAutomation(entity);
    }
  };

  const conversationStatusTag = useMemo(() => {
    if (!activeConversationInfo || isRequestsTab) {
      return;
    }

    const { status, livechatParticipants, livechatRequests } = activeConversationInfo;
    if (status === CONVERSATION_STATUSES.FINISHED) {
      return 'Finished';
    }

    if (livechatParticipants?.length && livechatSettings?.livechatMode === LIVECHAT_MODES.SEMI_PRIVATE) {
      const me = livechatParticipants.find(agent => agent.platformUserId === currentUser.id && agent.status === 'transferred');
      if (me) {
        return 'Transferred';
      }
    }

    if (livechatParticipants?.length && livechatRequests?.length) {
      const latestTransfer = livechatRequests.reduce((prev, curr) => {
        return moment(prev.requestedDate).isAfter(curr.requestedDate) ? prev : curr;
      });
      const me = livechatParticipants.find(agent => agent.platformUserId === currentUser.id && agent.status === 'active' && agent.isOwner);
      if (latestTransfer?.status === 'new' && latestTransfer.type === 'transfer' && me) {
        return 'Pending';
      }
    }

    return 'Active';
  }, [activeConversationInfo?.livechatParticipants, activeConversationInfo?.livechatRequests, livechatSettings?.livechatMode]);

  const onTranscriptExportCsv = async () => {
    const result = await exportConversationTranscriptCsv({ orgId, urlParams: {
      conversation: activeConversationInfo.id,
      order: 'ASC'
    } }).unwrap();

    const file = revertBlobTypes(result, 'application/json');

    if (file) {
      const fileName = file.headers['content-disposition']?.split(new RegExp(['"'], 'g'))?.[1] ?? `transcript.csv`;
      handleFileDownload(file.blob, fileName);
    }
  };

  const onTranscriptExportPdf = async () => {
    const result = await exportConversationTranscriptPdf({ orgId, urlParams: {
      conversation: activeConversationInfo.id,
      order: 'ASC'
    } }).unwrap();

    const file = revertBlobTypes(result, 'application/pdf');

    if (file) {
      const fileName = file.headers['content-disposition']?.split(new RegExp(['"'], 'g'))?.[1] ?? `transcript.pdf`;
      handleFileDownload(file.blob, fileName);
    }
  };

  // TODO: Statuses should be an enum
  const renderConversationStatus = () => {
    const tags = [];

    switch (conversationStatusTag) {
      case 'Active':
        tags.push(<ConversationStatusTag type="active" />);
        break;
      case 'Finished':
        tags.push(<ConversationStatusTag type="finished" />);
        break;
      case 'Transferred':
        tags.push(<ConversationStatusTag type="transferred" />);
        break;
      case 'Pending':
        tags.push(<ConversationStatusTag type="new" />);
        tags.push(<ConversationStatusTag type="transfer" />);
        break;
      default:
        break;
    }

    if (livechatSettings?.livechatMode === LIVECHAT_MODES.PUBLIC
      && activeConversationInfo?.livechatParticipants.find(participant => participant.platformUserId === currentUser.id)
    ) {
      tags.push(<ConversationStatusTag type="joined" />);
    }

    return <Typography.Paragraph strong className="mb-0 overflow-x-clip">{tags}</Typography.Paragraph>;
  };

  const renderRequestStatus = () => {
    let tags = [];
    const { status, type } = selectedRequest;

    if (status === REQUEST_STATUSES.ACCEPTED) {
      tags.push(<ConversationStatusTag type="accepted" />);
    } else if (status === REQUEST_STATUSES.MISSED) {
      tags.push(<ConversationStatusTag type="missed" />);
    } else {
      tags.push(<ConversationStatusTag type="new" />);
    }

    if (type === 'transfer') {
      tags.push(<ConversationStatusTag type="transfer" />);
    } else if (type === 'invitation') {
      tags.push(<ConversationStatusTag type="joined" title={t('Invite')} />);
    }
    return tags;
  };

  const SidebarSectionHeader = (Icon, title) => (
    <Fragment>
      <Space
        size={16}
        wrap={false}
        align="center"
        className="mb-4"
      >
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => {
            setActiveTab(null);
            if (isEditingResponses) {
              setIsEditingResponses(false);
            }
            if (isEditingAutomations) {
              setIsEditingAutomations(false);
            }
          }}
        />
        <Icon className="text-link bg-layout p-2 fs-lg border-radius-base" />

        <Typography.Text className="fs-lg text-primary">{title}</Typography.Text>
      </Space>

      <Divider
        className="mt-0 mb-4"
        style={{ width: 'calc(100% + 32px)', marginLeft: -16 }}
      />
    </Fragment>
  );

  const PreChatSurveyPanel = (item) => {
    if (!item) {
      return (
        <Row
          justify="center"
          align="middle"
          className="h-100"
        >
          <Spin />
        </Row>
      );
    }

    if (!item.metadata || !item.metadata?.preLiveChatSurvey) {
      return (
        <Empty className="text-muted fs-sm fw-bold" description={t('No survey info found')}/>
      );
    }

    return item.metadata.preLiveChatSurvey.map((question, index, array) => (
      <Fragment
        key={index}
      >
        <Row
          align="top"
          wrap={false}
          className="mb-1"
        >
          <Typography.Text style={{ minWidth: 48 }} className="fw-bold fs-sm text-muted">{t('Αgent')}:</Typography.Text>
          <Typography.Text className="fs-sm">{question.question}</Typography.Text>
        </Row>
        <Row
          align="top"
          wrap={false}
        >
          <Typography.Text style={{ minWidth: 48 }} className="fw-bold fs-sm text-muted">{t('User')}:</Typography.Text>
          <Typography.Text className="fs-sm" ellipsis>{question.answer}</Typography.Text>
        </Row>
        {
          index !== array.length - 1 ? (
            <Divider className="my-3"/>
          ) : null
        }
      </Fragment>
    ));
  };

  const renderEntityInfoPanel = () => {
    if (isRequestsTab) {
      return (
        <Collapse.Panel
          key="conversationDetails"
          header={<Typography.Text strong>{t('Request Details')}</Typography.Text>}
          className="ant-collapse-p-0"
          style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}
        >
          <div className="pb-4 px-6">
            <Row
              wrap={false}
              align="middle"
              className="mb-2"
            >
              <Typography.Text style={{ minWidth: 102 }} className="fw-bold fs-sm text-muted">{t('Status')}:</Typography.Text>
              {
                selectedRequest ? (
                  renderRequestStatus()
                ) : (
                  <Skeleton.Button size="small" className="p-0 m-0"/>
                )
              }
            </Row>

            <Row
              wrap={false}
              align="middle"
              className="mb-2"
            >
              <Typography.Text style={{ minWidth: 104 }} className="fw-bold fs-sm text-muted">{t('ID')}:</Typography.Text>

              {
                selectedRequest ? (
                  <Typography.Text
                    className="fs-sm"
                    ellipsis
                  >
                    {selectedRequest?.id}
                  </Typography.Text>
                ) : (
                  <Skeleton.Button size="small" className="p-0 m-0"/>
                )
              }
            </Row>

            <Row
              wrap={false}
              align="start"
              className="mb-2"
            >
              <Typography.Text style={{ minWidth: 104 }} className="fw-bold fs-sm text-muted">{t('Platform origin')}:</Typography.Text>

              {
                selectedRequest ? (
                  <Typography.Paragraph ellipsis={{ rows: 2 }} className="fs-sm mb-0">
                    {
                      isValidURL(selectedRequest.platformOrigin) ? (
                        <a target="_blank" rel="noopener noreferrer" href={selectedRequest.platformOrigin}>{selectedRequest.platformOrigin}</a>
                      ) : selectedRequest.platformOrigin ?? t('Unknown')
                    }
                  </Typography.Paragraph>
                ) : (
                  <Skeleton.Button size="small" className="p-0 m-0"/>
                )
              }
            </Row>

            <Row
              wrap={false}
              align="middle"
              className="mb-2"
            >
              {
                selectedRequest?.status === REQUEST_STATUSES.ACCEPTED ? (
                  <Fragment>
                    <Typography.Text style={{ minWidth: 102 }} className="fw-bold fs-sm text-muted">{t('Accepted by')}:</Typography.Text>

                    <Tooltip
                      title={selectedRequest.acceptedBy}
                      mouseEnterDelay={0.5}
                    >
                      <ConversationStatusTag
                        type="accepted"
                        title={
                          <Typography.Text ellipsis className="text-inherit" style={{ maxWidth: 100 }}>
                            {selectedRequest.acceptedBy ?? t('Unknown user')}
                          </Typography.Text>
                        }
                      />
                    </Tooltip>
                  </Fragment>
                ) : selectedRequest && selectedRequest.type !== 'external' && (
                  <Fragment>
                    <Typography.Text style={{ minWidth: 102 }} className="fw-bold mr-2 fs-sm text-muted">{selectedRequest?.type === 'transfer' ? t('Transferred from:') : t('Invited by:')}</Typography.Text>

                    <Tooltip
                      title={selectedRequest?.from?.fullName}
                      mouseEnterDelay={0.5}
                    >
                      <ConversationStatusTag
                        title={
                          <Typography.Text ellipsis className="text-inherit" style={{ maxWidth: 100 }}>
                            {selectedRequest?.from?.fullName ?? t('Unknown user')}
                          </Typography.Text>
                        }
                        type={selectedRequest?.type === 'transfer' ? 'transferred' : 'joined'}
                      />
                    </Tooltip>
                  </Fragment>
                )
              }
            </Row>
          </div>
        </Collapse.Panel>
      );
    }

    // Else, we're in the active conversation tab
    return (
      <Collapse.Panel
        key="conversationDetails"
        header={<Typography.Text strong>{t('Conversation Details')}</Typography.Text>}
        className="ant-collapse-p-0"
        style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}
      >
        <div
          className="pb-4 px-6 overflow-y-auto overflow-x-hidden"
          style={{ maxHeight: 280 }}
        >
          <Row
            wrap={false}
            align="middle"
            className="mb-2"
          >
            <Typography.Text style={{ minWidth: 102 }} className="fw-bold fs-sm text-muted">{t('Status')}:</Typography.Text>

            {
              activeConversationInfo ? (
                renderConversationStatus()
              ) : (
                <Skeleton.Button size="small" className="p-0 m-0"/>
              )
            }
          </Row>

          <Row
            wrap={false}
            align="middle"
            className="mb-2"
          >
            <Typography.Text style={{ minWidth: 104 }} className="fw-bold fs-sm text-muted">{t('ID')}:</Typography.Text>
            {
              activeConversationInfo ? (
                <Typography.Text className="fs-sm" ellipsis>{activeConversationInfo.id}</Typography.Text>
              ) : (
                <Skeleton.Button size="small" className="p-0 m-0"/>
              )
            }
          </Row>

          <Row
            wrap={false}
            align="middle"
            className="mb-2"
          >
            <Typography.Text style={{ minWidth: 104 }} className="fw-bold fs-sm text-muted">{t('Assigned to')}:</Typography.Text>
            {
              activeConversationInfo ? (
                <Typography.Text className="fs-sm" ellipsis>{activeConversationInfo.livechatParticipants.find(participant => participant.isOwner)?.fullName ?? t('Unknown')}</Typography.Text>
              ) : (
                <Skeleton.Button size="small" className="p-0 m-0"/>
              )
            }
          </Row>

          <Row
            wrap={false}
            align="start"
            className="mb-2"
          >
            <Typography.Text style={{ minWidth: 104 }} className="fw-bold fs-sm text-muted">{t('Platform origin')}:</Typography.Text>
            {
              activeConversationInfo ? (
                <Typography.Paragraph ellipsis={{ rows: 2 }} className="fs-sm mb-0">
                  {
                    isValidURL(activeConversationInfo.platformOrigin) ? (
                      <a target="_blank" rel="noopener noreferrer" href={activeConversationInfo.platformOrigin}>{activeConversationInfo.platformOrigin}</a>
                    ) : activeConversationInfo.platformOrigin ?? t('Unknown')
                  }
                </Typography.Paragraph>
              ) : (
                <Skeleton.Button size="small" className="p-0 m-0"/>
              )
            }
          </Row>

          <Row
            wrap={false}
            align="top"
            className="mb-2"
          >
            <Typography.Text style={{ minWidth: 104 }} className="fw-bold fs-sm text-muted">{t('Live Agents')}:</Typography.Text>
            <div className="fs-sm">
              {AgentList(activeConversationInfo?.livechatParticipants.filter(participant => participant.participantType === LIVECHAT_PARTICIPANT_TYPES.AGENT))}
            </div>
          </Row>

          <Row
            wrap={false}
            align="top"
            className="mb-2"
          >
            <Typography.Text style={{ minWidth: 104 }} className="fw-bold fs-sm text-muted">{t('Flags')}:</Typography.Text>

            {
              activeConversationInfo?.livechatParticipants ? (
                <ResponsiveTagsList
                  values={activeConversationInfo.livechatParticipants.find(participant => participant.platformUserId === currentUser.id)?.flags.map((flag, index) => ({
                    label: (
                      <Space>
                        <Space.Compact className="align-items-baseline">
                          <ImportantFlag /> {capitalize(flag)}
                        </Space.Compact>
                      </Space>
                    ), value: index
                  }))}
                  mode="tags"
                  className="w-100"
                  onInputKeyDown={(e) => {
                    if (!ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX.test(e.key)) {
                      e.preventDefault();
                      return false;
                    }
                  }}
                  tagRender={(tag) => <span className="ant-select-selection-item p-0 px-1 mr-2">{tag.label}</span>}
                />
              ) : (
                <Skeleton.Button size="small" className="p-0 m-0"/>
              )
            }
          </Row>

          <Row
            wrap={false}
            align="top"
            className="mb-2"
          >
            <Typography.Text style={{ minWidth: 104 }} className="fw-bold fs-sm text-muted">{t('Tags')}:</Typography.Text>

            {
              activeConversationInfo && !isLoadingSessionTags ? (
                <Select
                  className="w-100"
                  placeholder="Add tags"
                  disabled={!hasTags}
                  value={hasTags ? [...new Set(sessionTags)] : []}
                  mode="tags"
                  onChange={(value) => updateSessionTags({ orgId, tenantId: activeConversationInfo.tenantId, sessionId: activeConversationInfo?.platformSessionId, data: value })}
                  onSelect={(value) => {
                    if (organizationTags.find(tag => tag.label !== value)) {
                      updateOrganizationTags({ orgId, data: { label: value, scope: 'CHAT_SESSIONS' } });
                    }
                  }}
                  maxTagTextLength={15}
                  options={([...new Set(organizationTags?.map(tag => tag.label))] ?? []).map((label) => ({
                    label: label,
                    value: label
                  }))}
                  onInputKeyDown={(e) => {
                    if (!ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX.test(e.key)) {
                      e.preventDefault();
                      return false;
                    }
                  }}
                />
              ) : (
                <Skeleton.Button size="small" className="p-0 m-0"/>
              )
            }
          </Row>
        </div>
      </Collapse.Panel>
    );
  };

  const AgentList = (agents) => {
    if (is.array(agents)) {
      const list = [];
      const indexToSlice = 3;
      const agentsToShow = agents.slice(0, indexToSlice);
      const rest = agents.slice(indexToSlice);

      for (const agent of agentsToShow) {
        list.push(
          <Typography.Paragraph
            className="mb-0"
            key={agent.id}
            ellipsis
          >
            {agent.fullName}
          </Typography.Paragraph>
        );
      }

      if (rest.length) {
        list.push(
          <Tooltip
            placement="bottom"
            title={
              <Fragment>
                {
                  rest.map(agent => (
                    <Typography.Paragraph
                      key={agent.id}
                      ellipsis
                      className="text-muted mb-0"
                    >
                      {agent.fullName}
                    </Typography.Paragraph>
                  ))
                }
              </Fragment>
            }
          >
            <span className="bg-body py-1 px-2 mt-1 border-radius-base d-inline-block">
              +{rest.length}
            </span>
          </Tooltip>
        );
      }

      return list;
    }

    return <Skeleton.Button size="small" />;
  };

  const renderSidebarContent = () => {
    if (isEditingResponses && !isRequestsTab) {
      return (
        <Fragment>
          {SidebarSectionHeader(MessageOutlined, 'Canned Responses')}
          <Typography.Paragraph
            className="fs-sm text-muted fw-bold"
          >
            {t('You can also quickly access canned responses by typing')} "/"
          </Typography.Paragraph>

          <PredefinedEntitiesPanel
            onEntityTrigger={triggerEntity}
            hasSearch
            isEditMode
            predefinedEntity="responses"
            conversationId={activeConversationInfo?.tenantId}
          />
        </Fragment>
      );
    }

    const handleMenuClick = (filetype) => {
      if (filetype.key === 'csv') {
        onTranscriptExportCsv();
      } else if(filetype.key ==='pdf'){
        onTranscriptExportPdf();
      }
    };

    const menu = () => (
      <Menu
        onClick={(filetype) => handleMenuClick(filetype)}
        items={[
          {
            label: t('Choose your preferred format'),
            type: 'group'
          },
          {
            label: t('CSV'),
            key: 'csv'
          },
          {
            label: t('PDF'),
            key: 'pdf'
          }
        ]}
      />
    );

    if (isEditingAutomations &&!isRequestsTab) {
      return (
        <Fragment>
          {SidebarSectionHeader(AppstoreAddOutlined, 'Automations')}
          <Typography.Paragraph
            className="fs-sm text-muted fw-bold"
          >
            {t('You can also quickly access automations by typing')} "/"
          </Typography.Paragraph>

          <PredefinedEntitiesPanel
            onEntityTrigger={triggerEntity}
            isEditMode
            predefinedEntity="automations"
            conversationId={activeConversationInfo?.tenantId}
          />
        </Fragment>
      );
    }

    switch (activeTab) {
      case TABS.LEAVE:
        if (!isRequestsTab) {
          return (
            <Fragment>
              {SidebarSectionHeader(LeaveConversationIcon, t('Leave Conversation'))}
              <InviteAgentsPanel
                transferAndLeave={true}
                isInactive={isInactive}
                hasJoined={hasJoined}
              />
            </Fragment>
          );
        }
        break;

      case TABS.INVITES:
        if (!isRequestsTab) {
          return (
            <Fragment>
              {SidebarSectionHeader(UserAddOutlined, t('Invite LiveChat Agents'))}
              <InviteAgentsPanel
                transferAndLeave={true}
                isInactive={isInactive}
                hasJoined={hasJoined}
              />
            </Fragment>
          );
        }
        break;

      case TABS.AUTOMATIONS:
        if (!isRequestsTab) {
          return (
            <Fragment>
              {SidebarSectionHeader(AppstoreAddOutlined, t('Automations'))}
              <Row
                wrap={false}
                className="flex-column h-100 pb-2"
              >
                <Col
                  flex="auto"
                >
                  {
                    automations && automations.length ? (
                      <Fragment>
                        <Typography.Paragraph
                          className="fs-sm text-muted fw-bold"
                        >
                          {t('You can also quickly access automations by typing')} "/"
                        </Typography.Paragraph>

                        <Space wrap={true}>
                          {
                            automations.map(automation => (
                              <Space
                                key={automation.id}
                                wrap={false}
                                align="center"
                                size={2}
                              >
                                <Button
                                  type={selectedAutomation?.id === automation.id ? 'primary' : 'text'}
                                  size="small"
                                  disabled={isTriggeringAutomation}
                                  className={classNames('fs-sm', { 'automation-button': selectedAutomation?.id !== automation.id })}
                                  onClick={() => setSelectedAutomation(selectedAutomation?.id === automation.id ? null : automation)}
                                >
                                  {automation.name}
                                </Button>

                                <Button
                                  type="link"
                                  icon={organizationFavoriteAutomations.find(favorite => favorite.id === automation.id) ? <StarFilled className="favorite" /> : <StarOutlined />}
                                  onClick={() => {
                                    const favorites = JSON.parse(window.localStorage.getItem(STORAGE_KEYS.LIVECHAT_FAVORITE_AUTOMATIONS)) || [];
                                    const orgFavorites = favorites.find(automationGroup => automationGroup.orgId === orgId)?.automations ?? [];
                                    const favoritesToUpdate = [
                                      {
                                        orgId,
                                        automations: orgFavorites.find(item => item.id === automation.id)
                                          ? orgFavorites.filter(item => item.id !== automation.id )
                                          : uniqueBy([{ id: automation.id, name: automation.name }, ...orgFavorites], 'id')
                                      },
                                      ...favorites
                                    ];
                                    dispatch(updateFavoriteAutomations(favoritesToUpdate));
                                  }}
                                />
                              </Space>
                            ))
                          }
                        </Space>
                      </Fragment>
                    ) : (
                      <Typography.Paragraph
                        className="fs-sm text-muted fw-bold"
                      >
                        {t('There are no available automations for this Workspace')}
                      </Typography.Paragraph>
                    )
                  }
                </Col>
                <Col>
                  <Tooltip
                    title={t('Only conversation participants can trigger automations during active conversations.')}
                    trigger={activeConversationInfo && !hasJoined && !isInactive ? 'hover' : ''}
                    destroyTooltipOnHide
                    mouseEnterDelay={0.5}
                  >
                    <Button
                      type="primary"
                      size="large"
                      block
                      disabled={!selectedAutomation || !activeConversationInfo || (!hasJoined && !isInactive)}
                      loading={isTriggeringAutomation}
                      onClick={() => {
                        if (selectedAutomation?.agentInput && selectedAutomation.agentInput.length) {
                          setCustomAutomation(selectedAutomation);
                        } else {
                          onAutomationTrigger();
                        }
                      }}
                    >
                      {t('Trigger Automation')}
                    </Button>
                  </Tooltip>
                </Col>
              </Row>
            </Fragment>
          );
        }
        break;

      case TABS.EXPORT:
        if (!isRequestsTab) {
          return (
            <Fragment>
              {SidebarSectionHeader(DownloadOutlined, t('Export Transcript'))}
              <Typography.Paragraph className="text-muted fs-sm fw-bold mb-4">{t('Export conversation transcript')} (.csv) or (.pdf)</Typography.Paragraph>

              <Dropdown overlay={menu} trigger={['click']}>
                <Button
                  type="primary"
                  loading={isExportingTranscript}
                  className="mb-2"
                  block
                  size="large"
                >
                  {t('Download Transcript')}
                </Button>
              </Dropdown>
            </Fragment>
          );
        }
        break;

      case TABS.ASSISTANT:
        if (!isRequestsTab) {
          return (
            <Fragment>
              {SidebarSectionHeader(AiAssistantIcon, t('Agent Assistant'))}
              <AiAssistant deploymentId={livechatSettings?.botAssistantSettings?.botDeploymentId} />
            </Fragment>
          );
        }
        break;

      // Render all the icons (the default state)
      default:
        return (
          <Fragment>
            <Row
              wrap={false}
              justify="space-between"
              align="middle"
            >
              <Col className="mb-4">
                {
                  isRequestsTab ? null : (
                    <Space
                      size={16}
                      wrap={false}
                      align="center"
                      className="sidebar-automations"
                    >

                      {
                        IS_LIVECHAT_ASSISTANT_ENABLED && livechatSettings?.botAssistantSettings?.enabled && assistantDeploymentExists ? (
                          <Tooltip
                            title={t('LiveChat Agent\'s AI Assistant')}
                            mouseEnterDelay={0.75}
                          >
                            <Button
                              type="text"
                              icon={<AiAssistantIcon />}
                              className="text-link bg-layout"
                              disabled={isInactive || !hasJoined || !activeConversationInfo}
                              onClick={() => setActiveTab(TABS.ASSISTANT)}
                            />
                          </Tooltip>
                        ) : null
                      }

                      <Tooltip
                        title={t('Invite LiveChat Agents')}
                        mouseEnterDelay={0.75}
                      >
                        <Button
                          type="text"
                          icon={<UserAddOutlined />}
                          className="text-link bg-layout"
                          disabled={isInactive || !hasJoined || !activeConversationInfo}
                          onClick={() => setActiveTab(TABS.INVITES)}
                        />
                      </Tooltip>

                      {/* TODO: Uncomment on new implementation */}
                      {/* <Tooltip
                          title={t('Automations')}
                          mouseEnterDelay={0.75}
                        >
                          <Button
                            type="text"
                            icon={<AppstoreAddOutlined />}
                            className="text-link bg-layout"
                            disabled={(!hasJoined && !isInactive) || !activeConversationInfo}
                            onClick={() => setActiveTab(TABS.AUTOMATIONS)}
                          />
                        </Tooltip> */}

                      <Tooltip
                        title={t('Export Transcript')}
                        mouseEnterDelay={0.75}
                      >
                        <Button
                          type="text"
                          icon={<DownloadOutlined />}
                          className="text-link bg-layout"
                          disabled={!activeConversationInfo}
                          onClick={() => setActiveTab(TABS.EXPORT)}
                        />
                      </Tooltip>

                      {
                        !isOwner ? (
                          <Popconfirm
                            trigger="click"
                            overlayClassName="popconfirm-with-overlay"
                            getPopupContainer={() => endPopoverContainerRef.current}
                            onOpenChange={(open) => {
                              const { current: ref } = endPopoverContainerRef;
                              if (!ref) {
                                return;
                              }

                              if (open) {
                                ref.classList.add('visible');
                                document.body.classList.add('overflow-y-hidden');
                              } else {
                                ref.classList.remove('visible');
                                document.body.classList.remove('overflow-y-hidden');
                              }
                            }}
                            disabled={isInactive || !hasJoined || !activeConversationInfo}
                            icon={null}
                            title={
                              <Fragment>
                                <Typography.Paragraph className="fw-500">
                                  <LeaveConversationIcon className="mr-2"/>
                                  {t('Leave Conversation')}
                                </Typography.Paragraph>
                                <Typography.Paragraph className="text-center">{t('Are you sure you want to leave this conversation?')}</Typography.Paragraph>
                              </Fragment>
                            }
                            placement="bottomRight"
                            destroyTooltipOnHide
                            showArrow={false}
                            okButtonProps={{
                              size: 'large'
                            }}
                            okText={t('Leave Conversation')}
                            onConfirm={() => {
                              dispatch(leaveConversation(activeConversationInfo?.id));
                            }}
                            cancelButtonProps={{
                              size: 'large',
                              ghost: 'true'
                            }}
                          >
                            <Tooltip
                              title={t('Leave Conversation')}
                              mouseEnterDelay={0.75}
                            >
                              <Button
                                type="text"
                                icon={<LeaveConversationIcon />}
                                className="text-link bg-layout"
                                disabled={isInactive || !hasJoined || !activeConversationInfo || !hasMultipleActiveAgents}
                              />
                            </Tooltip>
                          </Popconfirm>
                        ) : (
                          <Tooltip
                            title={t('Leave Conversation')}
                            mouseEnterDelay={0.75}
                          >
                            <Button
                              type="text"
                              icon={<LeaveConversationIcon />}
                              className="text-link bg-layout"
                              disabled={isInactive || !hasJoined || !activeConversationInfo || !hasMultipleActiveAgents}
                              onClick={() => setActiveTab(TABS.LEAVE)}
                            />
                          </Tooltip>
                        )
                      }

                      <Popconfirm
                        trigger="click"
                        overlayClassName="popconfirm-with-overlay"
                        getPopupContainer={() => endPopoverContainerRef.current}
                        onOpenChange={(open) => {
                          const { current: ref } = endPopoverContainerRef;
                          if (!ref) {
                            return;
                          }

                          if (open) {
                            ref.classList.add('visible');
                            document.body.classList.add('overflow-y-hidden');
                          } else {
                            ref.classList.remove('visible');
                            document.body.classList.remove('overflow-y-hidden');
                          }
                        }}
                        disabled={isInactive || !hasJoined || !activeConversationInfo}
                        icon={null}
                        title={
                          <Fragment>
                            <Typography.Paragraph className="fw-500">
                              <CloseSquareOutlined className="fs-lg text-warning mr-2" />
                              {t('End Conversation')}
                            </Typography.Paragraph>
                            <Typography.Paragraph className="text-center">{t('Are you sure you want to terminate this conversation?')}</Typography.Paragraph>
                          </Fragment>
                        }
                        placement="bottomRight"
                        destroyTooltipOnHide
                        showArrow={false}
                        okButtonProps={{
                          block: true,
                          size: 'large',
                          type: 'danger'
                        }}
                        okText={t('End Conversation')}
                        onConfirm={() => {
                          setSidebarOpen(false);
                          dispatch(terminateConversation(activeConversationInfo?.id));
                        }}
                        cancelButtonProps={{
                          block: true,
                          size: 'large',
                          type: 'danger',
                          ghost: 'true'
                        }}
                      >
                        <Tooltip
                          title={t('End Conversation')}
                          mouseEnterDelay={0.75}
                        >
                          <Button
                            type="text"
                            icon={<CloseSquareOutlined />}
                            className="text-link bg-layout text-warning"
                            disabled={isInactive || !hasJoined || !activeConversationInfo}
                          />
                        </Tooltip>
                      </Popconfirm>
                    </Space>
                  )
                }
              </Col>

              <Col className="mb-4">
                <Button
                  type="text"
                  icon={<SidebarCollapseIcon className="fs-xl"/>}
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                />
              </Col>
            </Row>

            <Divider
              className="my-0"
              style={{ width: 'calc(100% + 32px)', marginLeft: -16 }}
            />

            {
              organizationFavoriteAutomations.length && !isRequestsTab ? (
                <Row>
                  <Col span={24}>
                    <Space
                      wrap
                      align="center"
                      className="mt-4 overflow-y-auto sidebar-automations"
                    >
                      {
                        organizationFavoriteAutomations.map(automation => (
                          <Button
                            type="text"
                            size="small"
                            disabled={!activeConversationInfo || (!hasJoined && !isInactive)}
                            className="fs-sm automation-button"
                            onClick={() => setCustomAutomation(automation)}
                          >
                            {automation.name}
                          </Button>
                        ))
                      }
                    </Space>
                  </Col>
                </Row>
              ) : null
            }

            {
              (!isRequestsTab && [400, 401, 403, 404].includes(fetchActiveConversationError?.error.status) && !activeConversationInfo) || (isRequestsTab && !selectedRequest && incomingRequests) ? (
                <Empty
                  className="py-10 text-muted"
                  description={isRequestsTab ? t('There is no information available for this request.') : t('There is no information available for this conversation.')}
                />
              ) : (
                <Collapse
                  ghost
                  destroyInactivePanel
                  defaultActiveKey={['userInfo']}
                  className="d-flex flex-column flex-auto livechat-sidebar-collapse"
                >
                  {renderEntityInfoPanel()}

                  <Collapse.Panel
                    key="userInfo"
                    header={<Typography.Text strong>{t('User Details')}</Typography.Text>}
                    className="ant-collapse-p-0"
                    style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}
                  >
                    <UserInfoPanel
                      item={isRequestsTab ? selectedRequest : activeConversationInfo}
                    />
                  </Collapse.Panel>

                  <Collapse.Panel
                    key="crmIntegration"
                    header={<Typography.Text strong>{t('Contact Info')}</Typography.Text>}
                    className="ant-collapse-p-0"
                    style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}
                  >
                    <CrmIntegrationsPanel conversation={isRequestsTab ? selectedRequest : activeConversationInfo} />
                  </Collapse.Panel>

                  <Collapse.Panel
                    key="extras"
                    header={<Typography.Text strong>{t('Request Metadata')}</Typography.Text>}
                    className="ant-collapse-p-0"
                    style={{ borderBottom: '1px solid rgba(0, 0, 0, 0.06)' }}
                  >
                    <div
                      className="mb-2 px-5"
                    >
                      {
                        hasExtras ? (
                          <JsonViewer jsonObject={conversationExtras}/>
                        ) : (
                          <Empty className="text-muted fs-sm fw-bold" description={t('No metadata found')}/>
                        )
                      }
                    </div>
                  </Collapse.Panel>

                  <Collapse.Panel
                    key="survey"
                    header={<Typography.Text strong>{t('Pre-Chat Survey')}</Typography.Text>}
                    className="ant-collapse-p-0"
                    style={!isRequestsTab ? { borderBottom: '1px solid rgba(0, 0, 0, 0.06)' } : {}}
                  >
                    <div
                      style={{ height: 164 }}
                      className="mt-4 pb-4 px-6 overflow-y-auto"
                    >
                      {
                        PreChatSurveyPanel(isRequestsTab ? selectedRequest : activeConversationInfo)
                      }
                    </div>
                  </Collapse.Panel>

                  {
                    !isRequestsTab ? (
                      <Collapse.Panel
                        key="notes"
                        header={<Typography.Text strong>{t('Conversation Notes')}</Typography.Text>}
                        className="ant-collapse-p-0 pb-5 conversation-notes-collapse"
                      >
                        <ConversationNotesPanel
                          hasJoined={hasJoined}
                        />
                      </Collapse.Panel>
                    ) : null
                  }
                </Collapse>
              )
            }
          </Fragment>
        );
    }
  };

  const onAutomationTrigger = async (automation = selectedAutomation) => {
    const { url, headers, customInputForm } = automation;
    const data = createAutomationBody(activeConversationInfo, customInputForm);
    triggerAutomation({ callbackUrl: url, callbackHeaders: headers, data: data, conversationId: activeConversationInfo?.id, automation: automation });
  };

  return (
    <div className="d-flex flex-column h-100 pt-4 px-4 overflow-y-auto overflow-x-hidden">
      {
        !isRequestsTab ? createPortal(<div ref={endPopoverContainerRef} className="end-conversation-popover-container"><div /></div>, document.body) : null
      }

      {renderSidebarContent()}

      {
        customAutomation ? (
          <CustomInputAutomationForm
            setAutomation={setCustomAutomation}
            automation={customAutomation}
            onTrigger={onAutomationTrigger}
          />
        ) : null
      }
    </div>
  );
};

export default memo(LiveChatSidebarRight);
